import { useEffect, useState, useRef, type SyntheticEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Grid, Skeleton, Tab, Tabs } from "@mui/material";
import { toast } from "react-toastify";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import QuizIcon from "@mui/icons-material/Quiz";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useLanguage } from "../../../context/LanguageContext";
import { useUser } from "../../../context/UserContext";
import { PageContainer } from "../../../components/Common";
import {
    useGetPersonById,
    useGetTransactions,
    useGetOffers,
    useUpdatePerson,
    useGetRealtimeTransactions,
    useGetRealtimeAvailableOffers,
    useAddPointsMovement,
    useAddManualTransaction,
    useGetTransactionDetails,
    useUpdateSubscription,
    useGetPersonWaitingUpdate,
    useMoveCustomerStore,
} from "../../../hooks/Clients/dashboard";
import type { Transaction } from "../../../hooks/Clients/dashboard";
import useGetActiveForms, { type FormAnswersMap } from "../../../hooks/Clients/dashboard/useGetActiveForms";
import { useStoreList } from "../../../context/StoreListContext";
import { CUSTOMER_ACTIONS, useRights } from "../../../rights";
import {
    buildFullName, buildAddress, toArray,
    type EditForm, type MvtForm, type ManualTransacForm,
    type IdentifierRaw, type AccountRaw, type SubscriptionRaw, type EventRaw, type GroupMemberRaw,
    type TxDetails, type Offer, type RealtimeOffer,
} from "./components/ui";
import useGetStoreById from "../../../hooks/Magasins/useGetStoreById";
import DashboardHeader from "./components/DashboardHeader";
import SynthesisTab from "./components/SynthesisTab";
import TransactionsTab from "./components/TransactionsTab";
import OperationsTab from "./components/OperationsTab";
import QuestionnaireTab from "./components/QuestionnaireTab";
import EditCustomerDialog from "./components/EditCustomerDialog";
import AddPointsDialog from "./components/AddPointsDialog";
import AddTransactionDialog from "./components/AddTransactionDialog";
import TransactionDetailDialog from "./components/TransactionDetailDialog";

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};

const readFlag = (value: unknown): boolean => value === "1" || value === 1 || value === true;


const getMainCommunication = (comms: Record<string, unknown>, keys: string[]): Record<string, unknown> => {
    for (const key of keys) {
        const value = asRecord(comms[key]);
        if (Object.keys(value).length > 0) return value;
    }
    return {};
};

const readCommunicationFlag = (
    communication: Record<string, unknown>,
    field: "ALLOW_COMPANY_SOLICITATION" | "ALLOW_TRUSTED_SOLICITATION",
    fallback?: boolean,
): boolean => {
    if (Object.keys(communication).length === 0) return fallback ?? false;
    return readFlag(communication[field]);
};

const CustomerDashboard = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { personId?: string | number; createdFormsData?: FormAnswersMap } | null;
    const { personId: personIdParam } = useParams<{ personId: string }>();
    const personId = locationState?.personId ?? personIdParam;
    const createdFormsData = locationState?.createdFormsData ?? {};
    const { selectedWorkspace } = useUser();
    const initialWorkspaceId = useRef<number | null>(null);

    useEffect(() => {
        if (!selectedWorkspace) return;
        if (initialWorkspaceId.current === null) {
            initialWorkspaceId.current = selectedWorkspace.id;
            return;
        }
        if (initialWorkspaceId.current !== selectedWorkspace.id) {
            navigate("/clients/search");
        }
    }, [selectedWorkspace, navigate]);

    const { customer, isLoading, error, getPersonById, invalidateCache, isReady: personReady } = useGetPersonById();
    const { transactions, isLoading: loadingTransac, getTransactions } = useGetTransactions();
    const { offers, isLoading: loadingOffers, getOffers } = useGetOffers();
    const { updatePerson, isLoading: savingEdit, error: editError, reset: resetEdit } = useUpdatePerson();
    const { activeForms, isLoading: loadingActiveForms, getActiveForms: loadActiveForms } = useGetActiveForms();
    const { realtimeTransactions, isLoading: loadingRealtimeTx, getRealtimeTransactions } = useGetRealtimeTransactions();
    const { realtimeOffers, isLoading: loadingRealtimeOffers, getRealtimeOffers } = useGetRealtimeAvailableOffers();
    const { addPointsMovement, isLoading: savingMvt, error: mvtError, reset: resetMvt } = useAddPointsMovement();
    const { addManualTransaction, isLoading: savingTransac, error: transacError, reset: resetTransac } = useAddManualTransaction();
    const { details: txDetails, isLoading: loadingTxDetails, getTransactionDetails, reset: resetTxDetails } = useGetTransactionDetails();
    const { updateSubscription } = useUpdateSubscription();
    const { stores } = useStoreList();
    const { waitingUpdate, getPersonWaitingUpdate } = useGetPersonWaitingUpdate();
    const { moveCustomerStore, isLoading: movingStore, error: moveStoreError } = useMoveCustomerStore();
    // Actions soumises aux droits BeOne : sans droit, le bouton disparaît (l'API refuserait l'appel)
    const { can } = useRights();
    const canMovePoints = can(CUSTOMER_ACTIONS.movePoints);
    const canAddManualTransaction = can(CUSTOMER_ACTIONS.addManualTransaction);
    const canChangeStore = can(CUSTOMER_ACTIONS.changeStore);
    const canEditDetails = can(CUSTOMER_ACTIONS.editDetails);
    const canFillQuestionnaire = can(CUSTOMER_ACTIONS.questionnaire);
    const { getStoreById, loading: loadingStore, data: storeData } = useGetStoreById();

    const [activeTab, setActiveTab] = useState(0);
    const [selectedTx, setSelectedTx] = useState<Transaction | null>(null);
    const [transacPage, setTransacPage] = useState(0);
    const [transacRowsPerPage, setTransacRowsPerPage] = useState(20);
    const [offerPage, setOfferPage] = useState(0);
    const [offerRowsPerPage, setOfferRowsPerPage] = useState(20);
    const [editOpen, setEditOpen] = useState(false);
    const [customerPatch, setCustomerPatch] = useState<Record<string, unknown> | null>(null);
    const [editForm, setEditForm] = useState<EditForm>({
        title_id: "", lastname: "", firstname: "", birthdate: "",
        address1: "", address2: "", address3_num: "", address3_type: "", address3_name: "",
        address4: "", zip_code: "", local_city_name: "", local_country_name: "",
        email_address: "", cell_phone_number: "", phone_number: "",
        mailCompany: false, emailCompany: false, smsCompany: false,
        mailTrusted: false, emailTrusted: false, smsTrusted: false,
        reason: "", storeId: "",
    });
    const originalStoreIdRef = useRef<string>("");
    const [mvtOpen, setMvtOpen] = useState(false);
    const [offersDialogOpen, setOffersDialogOpen] = useState(false);
    const [mvtForm, setMvtForm] = useState<MvtForm>({
        accountId: "",
        moveDate: new Date().toISOString().slice(0, 16),
        value: "",
        cause: "OUBLI CARTE",
        media: "AUTRE",
    });

    const [manualTransacOpen, setManualTransacOpen] = useState(false);
    const [manualTransacForm, setManualTransacForm] = useState<ManualTransacForm>({
        personIdentifierId: "",
        personId: "",
        entityId: "",
        dateTransaction: new Date().toISOString().slice(0, 16).replace("T", " ") + ":00",
        amount: "",
        currencyId: "1",
        cashDeskNumber: "",
        receiptNumber: "",
        offers: "",
        motif: "AUTRE",
        media: "AUTRE",
    });
    const [offerInput, setOfferInput] = useState("");
    const [offerChips, setOfferChips] = useState<string[]>([]);
    const [subscriptionOverrides, setSubscriptionOverrides] = useState<Record<string, Partial<SubscriptionRaw>>>({});
    const [loadingSubscriptionId, setLoadingSubscriptionId] = useState<string | null>(null);

    useEffect(() => {
        if (personId && personReady) {
            getPersonById(personId);
            getPersonWaitingUpdate(personId);
        }
    }, [personId, personReady]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!customer || !personId) return;
        const identifier = (customer.IDENTIFIER || customer.CARTE) as string;
        getTransactions(personId);
        getOffers(personId);
        // Questionnaires seulement avec le droit : l'API refuse /activeForms sinon
        if (canFillQuestionnaire) loadActiveForms(personId, createdFormsData);
        if (identifier) {
            getRealtimeTransactions(identifier);
            getRealtimeOffers(identifier);
        }
        const storeId = (customer.STORE_ID
            ?? (customer.ENT_ENTITY as Record<string, unknown>)?.ENTITY_ID) as string | number | undefined;
        if (storeId) getStoreById({ entity_id: storeId });
    }, [customer?.PERSONID]);

    const handleSelectTx = (tx: Transaction | null) => {
        setSelectedTx(tx);
        if (tx?.TRANSACTION_ID && personId) getTransactionDetails(personId, tx.TRANSACTION_ID);
        else resetTxDetails();
    };

    const handleTabChange = (_: SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        if (newValue === 1) setTransacPage(0);
        if (newValue === 2) setOfferPage(0);
    };

    const handleOpenEdit = () => {
        if (!customer) return;
        const rawCommsLocal = asRecord(customer.MAIN_COMMS);
        const postalAddrLocal = getMainCommunication(rawCommsLocal, ["MAIN_POSTAL_ADDRESS", "COMM_POSTAL_ADDRESS"]);
        const emailCommLocal = getMainCommunication(rawCommsLocal, ["MAIN_EMAIL", "COMM_EMAIL"]);
        const phoneCommLocal = getMainCommunication(rawCommsLocal, ["MAIN_PHONE", "COMM_PHONE"]);
        const cellCommLocal = getMainCommunication(rawCommsLocal, ["MAIN_CELL_PHONE", "MAIN_CELLPHONE", "MAIN_SMS", "COMM_CELL_PHONE", "COMM_SMS"]);
        const perLocal = asRecord(customer.PER_PERSON);
        const currentStoreId = String(
            customer.STORE_ID ?? (customer.ENT_ENTITY as Record<string, unknown>)?.ENTITY_ID ?? ""
        );
        originalStoreIdRef.current = currentStoreId;
        setEditForm({
            title_id: String(perLocal.TITLE_ID ?? customer.CIVILITY ?? ""),
            lastname: String(customer.LASTNAME ?? ""),
            firstname: String(customer.FIRSTNAME ?? ""),
            birthdate: (() => {
                const raw = String(customer.BIRTH_DATE ?? "");
                const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                return m ? `${m[3]}-${m[2]}-${m[1]}` : raw;
            })(),
            address1: String(postalAddrLocal.ADDRESS1 ?? customer.ADDRESS1 ?? ""),
            address2: String(postalAddrLocal.ADDRESS2 ?? customer.ADDRESS2 ?? ""),
            address3_num: String(postalAddrLocal.ADDRESS3_NUM ?? ""),
            address3_type: String(postalAddrLocal.ADDRESS3_TYPE ?? ""),
            address3_name: String(postalAddrLocal.ADDRESS3_NAME ?? ""),
            address4: String(postalAddrLocal.ADDRESS4 ?? customer.ADDRESS4 ?? ""),
            zip_code: String(postalAddrLocal.ZIP_CODE ?? customer.ZIP_CODE ?? ""),
            local_city_name: String(postalAddrLocal.LOCAL_CITY_NAME ?? customer.CITY ?? ""),
            local_country_name: String(postalAddrLocal.LOCAL_COUNTRY_NAME ?? customer.COUNTRY ?? ""),
            email_address: String(emailCommLocal.EMAIL_ADDRESS ?? customer.EMAIL ?? ""),
            cell_phone_number: String(cellCommLocal.CELL_PHONE_NUMBER ?? customer.CELLPHONE ?? ""),
            phone_number: String(phoneCommLocal.PHONE_NUMBER ?? customer.PHONE ?? ""),
            mailCompany: readCommunicationFlag(postalAddrLocal, "ALLOW_COMPANY_SOLICITATION"),
            emailCompany: readCommunicationFlag(emailCommLocal, "ALLOW_COMPANY_SOLICITATION"),
            smsCompany: readCommunicationFlag(cellCommLocal, "ALLOW_COMPANY_SOLICITATION"),
            mailTrusted: readCommunicationFlag(postalAddrLocal, "ALLOW_TRUSTED_SOLICITATION"),
            emailTrusted: readCommunicationFlag(emailCommLocal, "ALLOW_TRUSTED_SOLICITATION"),
            smsTrusted: readCommunicationFlag(cellCommLocal, "ALLOW_TRUSTED_SOLICITATION"),
            reason: "",
            storeId: currentStoreId,
        });
        resetEdit();
        setEditOpen(true);
    };

    const handleSaveEdit = async (data: EditForm) => {
        const bdMatch = data.birthdate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const birthDateOracle = bdMatch ? `${bdMatch[3]}/${bdMatch[2]}/${bdMatch[1]}` : data.birthdate;
        const currentPer = (customer as unknown as Record<string, unknown>)?.PER_PERSON as Record<string, unknown> ?? {};
        const patch: Record<string, unknown> = {
            FIRSTNAME: data.firstname,
            LASTNAME: data.lastname,
            BIRTH_DATE: birthDateOracle,
            ADDRESS1: data.address1,
            ADDRESS2: data.address2,
            ADDRESS3: [data.address3_num, data.address3_type, data.address3_name].filter(Boolean).join(" "),
            ADDRESS4: data.address4,
            ZIP_CODE: data.zip_code,
            CITY: data.local_city_name,
            COUNTRY: data.local_country_name,
            EMAIL: data.email_address,
            CELLPHONE: data.cell_phone_number,
            PHONE: data.phone_number,
            PER_PERSON: { ...currentPer, FIRSTNAME: data.firstname, LASTNAME: data.lastname, BIRTH_DATE: birthDateOracle },
        };
        setCustomerPatch(patch);
        setEditOpen(false);
        toast.success("Modifications enregistrées.");

        const ok = await updatePerson({ personId: personId!, ...data });
        if (ok) {
            // La fiche est gardée 2 min en sessionStorage : on la vide pour relire les données à jour
            invalidateCache(personId!);
            getPersonById(personId!).then(() => setCustomerPatch(null));
        } else {
            setCustomerPatch(null);
            toast.error(editError || "La mise à jour a échoué.");
        }

        if (canChangeStore && data.storeId && data.storeId !== originalStoreIdRef.current) {
            const identifierValue = String(customer?.IDENTIFIER ?? (customer?.CARTE as string) ?? "");
            const identifiersLocal = toArray<IdentifierRaw>(customer?.IDENTIFIERS ?? customer?.PER_PERSON_IDENTIFIER);
            const matchedIdentifier = identifiersLocal.find(id => id.IDENTIFIER_VALUE === identifierValue)
                ?? identifiersLocal.find(id => id.PRIORITY === "1")
                ?? identifiersLocal[0] ?? null;
            const personIdentifierTypeId = matchedIdentifier?.PERSON_IDENTIFIER_TYPE_ID;

            if (identifierValue && personIdentifierTypeId) {
                const moved = await moveCustomerStore({
                    identifierValue,
                    entityId: data.storeId,
                    motif: data.reason,
                    personIdentifierTypeId,
                });
                if (moved) {
                    invalidateCache(personId!);
                    toast.success("Changement de magasin enregistré, effectif sous 24h.");
                } else {
                    toast.error(moveStoreError || "Le changement de magasin a échoué.");
                }
            }
        }
    };

    if (!personId) {
        return (
            <PageContainer>
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Aucun client sélectionné.{" "}
                    <Button size="small" onClick={() => navigate("/clients/search")}>Retour à la recherche</Button>
                </Alert>
            </PageContainer>
        );
    }

    if (isLoading || (!customer && !error)) {
        return (
            <PageContainer>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <Skeleton variant="circular" width={56} height={56} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton width="40%" height={32} />
                        <Skeleton width="25%" height={20} />
                    </Box>
                </Box>
                <Skeleton variant="rounded" height={48} sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    {[1, 2, 3].map((i) => (
                        <Grid key={i} size={{ xs: 12, md: 4 }}>
                            <Skeleton variant="rounded" height={200} />
                        </Grid>
                    ))}
                </Grid>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer>
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/clients/search")} sx={{ mt: 2 }}>
                    {t("pages.customers.dashboard.back")}
                </Button>
            </PageContainer>
        );
    }

    if (!customer) {
        return (
            <PageContainer>
                <Alert severity="info" sx={{ mt: 2 }}>{t("pages.customers.dashboard.notFound")}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/clients/search")} sx={{ mt: 2 }}>
                    {t("pages.customers.dashboard.back")}
                </Button>
            </PageContainer>
        );
    }

    const displayCustomer: typeof customer = customerPatch
        ? { ...customer, ...customerPatch as Partial<typeof customer>, PER_PERSON: { ...(customer.PER_PERSON as Record<string, unknown> ?? {}), ...(customerPatch.PER_PERSON as Record<string, unknown> ?? {}) } } as typeof customer
        : customer;

    const fullName = buildFullName(displayCustomer);
    const fullAddress = buildAddress(displayCustomer);
    const cardNumber = displayCustomer.IDENTIFIER || (displayCustomer.CARTE as string) || null;

    const allIdentifiers = toArray<IdentifierRaw>(displayCustomer.IDENTIFIERS ?? displayCustomer.PER_PERSON_IDENTIFIER);
    const accounts = toArray<AccountRaw>(displayCustomer.PER_ACCOUNT ?? displayCustomer.ACCOUNTS);
    const mainAccount = (displayCustomer.MAIN_ACCOUNT as AccountRaw) || null;

    const rawComms = asRecord(displayCustomer.MAIN_COMMS);
    const rawPostalAddr = getMainCommunication(rawComms, ["MAIN_POSTAL_ADDRESS", "COMM_POSTAL_ADDRESS"]);
    const rawEmailComm = getMainCommunication(rawComms, ["MAIN_EMAIL", "COMM_EMAIL"]);
    const rawCellComm = getMainCommunication(rawComms, ["MAIN_CELL_PHONE", "MAIN_CELLPHONE", "MAIN_SMS", "COMM_CELL_PHONE", "COMM_SMS"]);
    const rawPhoneComm = getMainCommunication(rawComms, ["MAIN_PHONE", "COMM_PHONE"]);
    const npaiStatusId = rawPostalAddr.STATUS_ID as string | null;
    const npaiStatusDate = (rawPostalAddr.STATUS_D ?? rawPostalAddr.STATUS_DATE) as string | null;
    const postalStatusId = npaiStatusId;
    const emailStatusId = rawEmailComm.STATUS_ID as string | null;
    const emailStatusD = (rawEmailComm.STATUS_D ?? rawEmailComm.STATUS_DATE) as string | null;
    const cellStatusId = rawCellComm.STATUS_ID as string | null;
    const cellStatusD = (rawCellComm.STATUS_D ?? rawCellComm.STATUS_DATE) as string | null;
    const phoneStatusId = rawPhoneComm.STATUS_ID as string | null;
    const phoneStatusD = (rawPhoneComm.STATUS_D ?? rawPhoneComm.STATUS_DATE) as string | null;
    const cnilCompanyMail = readCommunicationFlag(rawPostalAddr, "ALLOW_COMPANY_SOLICITATION");
    const cnilCompanyEmail = readCommunicationFlag(rawEmailComm, "ALLOW_COMPANY_SOLICITATION");
    const cnilCompanySms = readCommunicationFlag(rawCellComm, "ALLOW_COMPANY_SOLICITATION");
    const cnilTrustedMail = readCommunicationFlag(rawPostalAddr, "ALLOW_TRUSTED_SOLICITATION");
    const cnilTrustedEmail = readCommunicationFlag(rawEmailComm, "ALLOW_TRUSTED_SOLICITATION");
    const cnilTrustedSms = readCommunicationFlag(rawCellComm, "ALLOW_TRUSTED_SOLICITATION");


    const subscriptions = toArray<SubscriptionRaw>(displayCustomer.PER_PERSON_SUBSCRIPTION).map(sub => ({
        ...sub,
        ...(sub.PER_PERSON_SUBSCRIPTIONS_ID ? subscriptionOverrides[sub.PER_PERSON_SUBSCRIPTIONS_ID] : {}),
    })) as SubscriptionRaw[];

    const events = toArray<EventRaw>(displayCustomer.PER_PERSON_EVENTS ?? displayCustomer.PER_PERSON_EVENT);

    const groupMembers = toArray<GroupMemberRaw>(displayCustomer.GROUP_MEMBERS);

    const rawPer = (displayCustomer.PER_PERSON as Record<string, unknown>) || {};
    const score = (rawPer.SCORE ?? displayCustomer.SCORE) as number | string | null;

    const handleToggleSubscription = async (subscriptionId: string, currentValue: string, label: string) => {
        if (loadingSubscriptionId) return; // empêche les double-clics
        const newValue = currentValue === "1" ? "0" : "1";
        const today = new Date().toLocaleDateString("fr-FR");
        setLoadingSubscriptionId(subscriptionId);
        setSubscriptionOverrides(prev => ({ ...prev, [subscriptionId]: { IS_SUBSCRIBED: newValue, UPDATED_AT: today } }));
        const ok = await updateSubscription(subscriptionId, newValue === "1");
        setLoadingSubscriptionId(null);
        if (ok) {
            if (personId) invalidateCache(personId);
            toast.success(`L'abonnement "${label}" a bien été modifié en ${newValue === "1" ? "Abonné" : "Désabonné"}`);
        } else {
            setSubscriptionOverrides(prev => ({ ...prev, [subscriptionId]: { IS_SUBSCRIBED: currentValue } }));
            toast.error(`Échec de la modification de l'abonnement "${label}"`);
        }
    };

    const handleOpenMvt = () => {
        resetMvt();
        const firstAccount = accounts[0];
        setMvtForm({
            accountId: firstAccount ? String(firstAccount.ACCOUNT_ID ?? firstAccount.ACCOUNT_HEAD_ID ?? "") : "",
            moveDate: new Date().toISOString().slice(0, 16),
            value: "",
            cause: "OUBLI CARTE",
            media: "AUTRE",
        });
        setMvtOpen(true);
    };

    const handleSaveMvt = async (formOverride?: MvtForm) => {
        const form = formOverride ?? mvtForm;
        const ok = await addPointsMovement({
            accountId: isNaN(Number(form.accountId)) ? form.accountId : Number(form.accountId),
            moveDate: form.moveDate,
            value: Number(form.value),
            cause: form.cause,
            media: form.media,
        });
        if (ok) {
            if (personId) invalidateCache(personId);
            setMvtOpen(false);
            toast.success("Mouvement de points ajouté avec succès.");
        } else {
            toast.error(mvtError || "L'ajout du mouvement a échoué.");
        }
    };

    const handleOpenManualTransac = () => {
        resetTransac();
        const storeId = (customer?.STORE_ID
            ?? (customer?.ENT_ENTITY as Record<string, unknown>)?.ENTITY_ID) as string | number | undefined;
        setManualTransacForm({
            personIdentifierId: cardNumber || "",
            personId: String(customer?.PERSONID ?? ""),
            entityId: String(storeId ?? ""),
            dateTransaction: new Date().toISOString().slice(0, 16).replace("T", " ") + ":00",
            amount: "",
            currencyId: "1",
            cashDeskNumber: "",
            receiptNumber: "",
            offers: "",
            motif: "AUTRE",
            media: "AUTRE",
        });
        setOfferInput("");
        setOfferChips([]);
        setManualTransacOpen(true);
    };

    const handleSaveManualTransac = async (formOverride?: ManualTransacForm, offersOverride?: string[]) => {
        const form = formOverride ?? manualTransacForm;
        const ok = await addManualTransaction({
            ...form,
            offers: (offersOverride ?? offerChips).join(","),
        });
        if (ok) {
            setManualTransacOpen(false);
            toast.success("Transaction ajoutée avec succès.");
            if (personId) getTransactions(personId);
        } else {
            toast.error(transacError || "L'ajout de la transaction a échoué.");
        }
    };

    const storeForCard = storeData ?? (customer.ENT_ENTITY as import("../../../types/Store").Store | null);
    const storeCardLoading = loadingStore && !storeForCard;

    return (
        <PageContainer className="customer-dashboard-responsive-text">
            <Grid container spacing={2} alignItems="stretch">
                <Grid size={{ xs: 12 }}>
                    <DashboardHeader
                        customer={displayCustomer}
                        fullName={fullName}
                        cardNumber={cardNumber}
                        handleOpenEdit={canEditDetails ? handleOpenEdit : undefined}
                        waitingUpdate={waitingUpdate}
                        t={t}
                        accounts={accounts}
                        mainAccount={mainAccount}
                        workspaceVolumeName={selectedWorkspace?.volumeName}
                        npai={postalStatusId === "5"}
                        npaiDate={npaiStatusDate}
                        addressInvalid={postalStatusId === "6"}
                        deceased={postalStatusId === "8"}
                        emailInvalid={["4", "6", "10", "11", "12"].includes(emailStatusId ?? "")}
                        emailInvalidDate={emailStatusD}
                        mobileInvalid={cellStatusId === "4"}
                        mobileInvalidDate={cellStatusD}
                        phoneInvalid={phoneStatusId === "4"}
                        phoneInvalidDate={phoneStatusD}
                    />
                </Grid>
            </Grid>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t("pages.customers.dashboard.tabs.synthesis")} />
                    <Tab icon={<ReceiptLongIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t("pages.customers.dashboard.tabs.transactions")} />
                    <Tab icon={<AccountBalanceWalletIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Opérations" />
                    {/* Dernier onglet : le retirer ne décale pas les index des autres */}
                    {canFillQuestionnaire && <Tab icon={<QuizIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Questionnaire" />}
                </Tabs>
            </Box>

            <SynthesisTab
                activeTab={activeTab}
                store={storeData}
                loadingStore={loadingStore}
                customer={displayCustomer}
                fullAddress={fullAddress}
                postalStatusId={postalStatusId}
                emailStatusId={emailStatusId}
                emailStatusD={emailStatusD}
                cellStatusId={cellStatusId}
                cellStatusD={cellStatusD}
                phoneStatusId={phoneStatusId}
                phoneStatusD={phoneStatusD}
                cardNumber={cardNumber}
                allIdentifiers={allIdentifiers}
                accounts={accounts}
                mainAccount={mainAccount}
                npaiStatusDate={npaiStatusDate}
                cnilCompanyMail={cnilCompanyMail}
                cnilCompanyEmail={cnilCompanyEmail}
                cnilCompanySms={cnilCompanySms}
                cnilTrustedMail={cnilTrustedMail}
                cnilTrustedEmail={cnilTrustedEmail}
                cnilTrustedSms={cnilTrustedSms}
                subscriptions={subscriptions}
                events={events}
                groupMembers={groupMembers}
                score={score}
                realtimeTransactions={realtimeTransactions as Transaction[]}
                loadingRealtimeTx={loadingRealtimeTx}
                realtimeOffers={realtimeOffers as RealtimeOffer[]}
                loadingRealtimeOffers={loadingRealtimeOffers}
                offers={offers as Offer[]}
                loadingOffers={loadingOffers}
                transactions={transactions}
                loadingTransac={loadingTransac}
                setSelectedTx={handleSelectTx}
                setActiveTab={setActiveTab}
                handleOpenMvt={canMovePoints ? handleOpenMvt : undefined}
                handleOpenManualTransac={canAddManualTransaction ? handleOpenManualTransac : undefined}
                handleToggleSubscription={handleToggleSubscription}
                loadingSubscriptionId={loadingSubscriptionId}
                t={t}
                personId={personId}
            />

            <TransactionsTab
                activeTab={activeTab}
                transactions={transactions}
                loadingTransac={loadingTransac}
                transacPage={transacPage}
                setTransacPage={setTransacPage}
                transacRowsPerPage={transacRowsPerPage}
                setTransacRowsPerPage={setTransacRowsPerPage}
                setSelectedTx={handleSelectTx}
                handleOpenManualTransac={canAddManualTransaction ? handleOpenManualTransac : undefined}
                handleOpenMvt={canMovePoints ? handleOpenMvt : undefined}
                t={t}
            />
            <OperationsTab
                activeTab={activeTab}
                offers={offers as Offer[]}
                loadingOffers={loadingOffers}
                offerPage={offerPage}
                setOfferPage={setOfferPage}
                offerRowsPerPage={offerRowsPerPage}
                setOfferRowsPerPage={setOfferRowsPerPage}
                handleOpenMvt={canMovePoints ? handleOpenMvt : undefined}
                personId={personId}
                getOffers={getOffers}
                t={t}
            />

            {canFillQuestionnaire && <QuestionnaireTab
                activeTab={activeTab}
                personId={personId}
                activeForms={activeForms}
                loadingActiveForms={loadingActiveForms}
                onSaved={() => loadActiveForms(personId, {})}
            />}

            <EditCustomerDialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                fullName={fullName}
                initialValues={editForm}
                onSave={handleSaveEdit}
                savingEdit={savingEdit || movingStore}
                hasWaitingUpdate={Boolean(waitingUpdate?.hasWaitingUpdate)}
                t={t}
                stores={stores}
                canChangeStore={canChangeStore}
            />

            <AddPointsDialog
                open={mvtOpen}
                onClose={() => setMvtOpen(false)}
                accounts={accounts}
                mvtForm={mvtForm}
                setMvtForm={setMvtForm}
                handleSaveMvt={handleSaveMvt}
                savingMvt={savingMvt}
                mvtError={mvtError}
                hasWaitingUpdate={Boolean(waitingUpdate?.details.PER_ACCOUNT_MISC_MOVE)}
                hasAccount={displayCustomer.PER_ACCOUNT != null}
                t={t}
            />

            <AddTransactionDialog
                open={manualTransacOpen}
                onClose={() => setManualTransacOpen(false)}
                stores={stores ?? undefined}
                manualTransacForm={manualTransacForm}
                setManualTransacForm={setManualTransacForm}
                handleSaveManualTransac={handleSaveManualTransac}
                savingTransac={savingTransac}
                transacError={transacError}
                offerInput={offerInput}
                setOfferInput={setOfferInput}
                offerChips={offerChips}
                setOfferChips={setOfferChips}
                hasWaitingUpdate={Boolean(waitingUpdate?.details.TRA_MAN_TRANSAC)}
                t={t}
            />

            <TransactionDetailDialog
                selectedTx={selectedTx}
                setSelectedTx={handleSelectTx}
                txDetails={txDetails as TxDetails | null}
                loadingTxDetails={loadingTxDetails}
                offersDialogOpen={offersDialogOpen}
                setOffersDialogOpen={setOffersDialogOpen}
            />


        </PageContainer>
    );
};

export default CustomerDashboard;
